import { getClientWidth } from 'src/Foundation/Design/platform/app/utils/get-client-width';
import { CssClass, TopMenuEvent } from '@gn-audio/core/common/enums';

interface IMenuItemClickEvent extends Event {
  currentTarget: HTMLDivElement;
  target: HTMLDivElement;
}

((): void => {
  const activeClassName = 'active';                 // signalize if current menu item is active (on the menu item level)
  const hiddenClassName = 'element-hidden';                 // signalize if current menu item is active (on the menu item level)
  const menuBtnActiveClassName = 'active-btn';      // signalize if menu button is active/pressed (on the menu level)
  const menuItemActiveClassName = 'active-item';    // signalize if one menu item is active/pressed (on the menu level)
  const siteSelectorActiveRegionClassName = 'active-region';
  const focusedClassName = CssClass.Focused;
  const langItemClassName = '.lang-item';
  const searchItemClassName = '.search-item';
  const searchInputClassName = '.site-search__search__input';
  const searchResultsClassName = '.site-search__results--is-animated-open ';
  const itemDataClassName = '.data';
  const topMenuClassName = '.top-menu';
  const menuBtnClassName = `${topMenuClassName}__menu-btn`;
  const closeBtnClassName = `${topMenuClassName}__close-btn`;
  const topMenuItemClassName = `${topMenuClassName}__item`;
  const navClassName = `${topMenuClassName}__nav`;
  const navItemClassName = `.nav-item`;
  const siteSelectorRegionLinkClass = `top-menu__siteselector-v3__region__link`;
  const siteSelectorListClass = `top-menu__siteselector-v3__list`;

  const menuHeight = 60;
  const removeActiveClassesTimeout = 200;
  const desktopViewMinWidth = 992;
  let searchHeight = 76;

  const enum UserEvent { Click = 'click', Focus = 'focus', Blur = 'blur' }

  const topMenu: HTMLDivElement | null = document.querySelector(topMenuClassName);

  if (!topMenu) return;

  /**
   * Foreach implementation for node list.
   * @param elements List of HTML elements.
   * @param action Action to be executed for each HTML element.
   * @remarks Added because of IE11 support. :()
   */
  function forEach<T extends HTMLElement>(elements: NodeListOf<T>, action: (element: T) => void) {
    Array.prototype.forEach.call(elements, action);
  }

  const topMenuItems: HTMLElement[] = Array.prototype.slice.call(topMenu.querySelectorAll(topMenuItemClassName));
  const topMenuNavItems: NodeListOf<HTMLDivElement> = topMenu.querySelectorAll(`${topMenuItemClassName}${navItemClassName}`);
  const itemsData: NodeListOf<HTMLDivElement> = topMenu.querySelectorAll(`${topMenuItemClassName} ${itemDataClassName}`);
  const siteSelectorRegionLinkItems: NodeListOf<HTMLDivElement> = topMenu.querySelectorAll(`.${siteSelectorRegionLinkClass}`);
  const siteSelectorListItems: NodeListOf<HTMLDivElement> = topMenu.querySelectorAll(`.${siteSelectorListClass}`);
  const menuBtn: HTMLDivElement | null = topMenu.querySelector(menuBtnClassName);
  const closeBtn: HTMLDivElement | null = topMenu.querySelector(closeBtnClassName);
  const nav: HTMLDivElement | null = topMenu.querySelector(navClassName);
  const langItem: HTMLDivElement | null = topMenu.querySelector(langItemClassName);
  const searchItem: HTMLDivElement | null = topMenu.querySelector(searchItemClassName); // Search item is not available on search page.
  const searchInput: HTMLInputElement | null = (searchItem || document).querySelector(searchInputClassName);
  const searchResults: HTMLDivElement | null = (searchItem || document).querySelector(searchResultsClassName);

  const removeItemsActiveClasses = (): void => {
    topMenuItems.forEach((x: HTMLDivElement) => x.classList.remove(activeClassName));
    forEach(siteSelectorRegionLinkItems, (item: HTMLDivElement) => item.classList.remove(activeClassName));
    topMenu.classList.remove(menuItemActiveClassName);
  };

  const removeSiteSelectorActiveClasses = (): void => {
    forEach(siteSelectorListItems, (item: HTMLDivElement) => item.classList.remove(activeClassName));
    forEach(siteSelectorRegionLinkItems, (item: HTMLDivElement) => item.classList.remove(activeClassName));
  };

  const onMenuItemClick = (event: IMenuItemClickEvent): void => {
    const { currentTarget } = event;

    if (currentTarget.classList.contains(activeClassName)) {
      currentTarget.classList.remove(activeClassName);
      topMenu.classList.remove(menuItemActiveClassName);
      return;
    }

    removeItemsActiveClasses();
    currentTarget.classList.add(activeClassName);
    topMenu.classList.add(menuItemActiveClassName);
  };

  const onMenuBtnClick = (): void => {
    removeItemsActiveClasses();
    topMenu.classList.toggle(menuBtnActiveClassName);
    clearSearch();
  };

  const clearSearch = (): void => {
    searchResults.classList.add(hiddenClassName);
    searchInput.value = '';
  };

  const onMenuClose = (): void => {
    removeItemsActiveClasses();
    langItem.classList.remove(siteSelectorActiveRegionClassName);
    topMenu.classList.remove(menuBtnActiveClassName);
    removeSiteSelectorActiveClasses();
    clearSearch();
  };

  const onMenuBlur = (): void => {
    // do nothing on blur for mobile menu in case search is active
    if (searchInput?.value && (getClientWidth() < desktopViewMinWidth)) {
      return;
    }

    topMenu.classList.remove(focusedClassName);
    setTimeout((): void => {
      if (!topMenu.classList.contains(focusedClassName)) {
        removeItemsActiveClasses();
        topMenu.classList.remove(menuBtnActiveClassName);
      }
    }, removeActiveClassesTimeout);
  };

  const onMenuFocus = (): void => {
    topMenu.classList.add(focusedClassName);
  };

  const stopPropagation = (event: Event): void => {
    event.stopPropagation();
  };

  const setMenuBtnVisibility = (): void => {
    if (topMenuItems.length === 0) {
      menuBtn.style.visibility = 'hidden';
    }
  };

  const addEventListeners = (): void => {
    topMenu.addEventListener(UserEvent.Focus, onMenuFocus, true);
    topMenu.addEventListener(UserEvent.Blur, onMenuBlur, true);

    topMenu.addEventListener(TopMenuEvent.RegisterCustomTopMenuItem, (event: CustomEvent) => {
      if (!event.detail) {
        return;
      }

      registerCustomTopMenuItem(event.detail);
    });

    menuBtn?.addEventListener(UserEvent.Click, onMenuBtnClick);
    closeBtn?.addEventListener(UserEvent.Click, onMenuClose);
    topMenuItems.forEach(assignEventsToTopMenuItem);
    forEach(itemsData, (data: HTMLDivElement) => data.addEventListener(UserEvent.Click, stopPropagation));
  };

  // required for mobile view when menu button is pressed
  const calcTopCssValues = (): void => {
    if (searchItem) {
      searchItem.style.top = `${menuHeight}px`;
    } else {
      searchHeight = 0;
    }

    if (nav) {
      nav.style.top = `${menuHeight + searchHeight}px`;
    }

    if (langItem) {
      langItem.style.top = `${menuHeight * topMenuNavItems.length + menuHeight + searchHeight}px`;
    }
  };

  const assignEventsToTopMenuItem = (element: HTMLElement) => {
    element.addEventListener(UserEvent.Click, onMenuItemClick);
  };

  const registerCustomTopMenuItem = (element: HTMLElement) => {
    assignEventsToTopMenuItem(element);
    topMenuItems.push(element);
  };

  addEventListeners();
  calcTopCssValues();
  setMenuBtnVisibility();
})();
